import React, { Suspense, useEffect, useMemo, useState } from 'react'

import axios from 'axios'
import { DEFAULT_LOCALE } from 'config'
import PropTypes from 'prop-types'
import { IntlProvider } from 'react-intl'

import { isObject } from '@/utils/helpers/typeChecking'

import { useLanguage } from './LanguageContext'

const langStorage = {}
const asyncLangStorage = {}

const fetchTranslation = async (
  locale,
  localeBasePath = process.env.PUBLIC_URL,
) => {
  const path = `${localeBasePath}/lang/${locale}.json?ver=${VERSION}`
  if (langStorage[path]) {
    return langStorage[path]
  }
  if (asyncLangStorage[path]) {
    const { data } = await asyncLangStorage[path]
    return data
  }
  try {
    const query = axios.get(path)
    asyncLangStorage[path] = query
    const { data } = await query
    langStorage[path] = data
    return data
  } catch (e) {
    console.error(e)
    if (locale !== DEFAULT_LOCALE) {
      return await fetchTranslation(DEFAULT_LOCALE, localeBasePath)
    }
  }
  return {}
}

export const IntlContext = ({ children, options }) => {
  const { localeBasePath } = options ?? {}
  const { locale } = useLanguage()
  const [messages, setMessages] = useState(null)
  const [defaultMessages, setDefaultMessages] = useState({})

  useEffect(() => {
    const setTranslation = async () => {
      let defPromise
      if (DEFAULT_LOCALE !== locale && !Object.keys(defaultMessages).length) {
        defPromise = fetchTranslation(DEFAULT_LOCALE, localeBasePath)
      }
      setMessages(await fetchTranslation(locale, localeBasePath))
      if (defPromise) {
        setDefaultMessages(await defPromise)
      }
    }
    setTranslation()
  }, [defaultMessages, locale, localeBasePath])

  const allMessages = useMemo(() => {
    const currentMessages = isObject(messages) ? messages : {}
    return { ...defaultMessages, ...currentMessages }
  }, [defaultMessages, messages])

  if (messages === null) return null

  return (
    <Suspense>
      <IntlProvider locale={locale} messages={allMessages}>
        {children}
      </IntlProvider>
    </Suspense>
  )
}

IntlContext.propTypes = {
  children: PropTypes.any,
  options: PropTypes.object,
}
